import React from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { MaintenanceState } from "api/automationTypes";
import { BlockStandard } from "components/admin/BlockStandard";
import { PhotoUploadGrid } from "./PhotoUploadGrid";
import { DeleteCTA } from "components/MUIExtensions/DeleteCTA";

const CONSERVATION_STATES = Object.values(MaintenanceState);

export const HeatingWaterDetail = ({
  heatingWaterData,
  heatingWaterPos,
  onChange,
  onDelete,
}: HeatingWaterDetailProps) => {
  const handleFieldChange =
    (field: keyof HeatingWaterDetailItem) =>
    ({ target }) => {
      onChange({ ...heatingWaterData, [field]: target.value });
    };

  const handlePhotosChange = (photos: File[]) => {
    onChange({ ...heatingWaterData, images: photos });
  };

  return (
    <BlockStandard>
      <TextField
        fullWidth
        label="Tipo de sistema"
        value={heatingWaterData.systemType || ""}
        onChange={handleFieldChange("systemType")}
      />

      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        gap={2}
      >
        <TextField
          label="Modelo do coletor solar"
          placeholder="Informe"
          value={heatingWaterData.solarCollectorModel || ""}
          onChange={handleFieldChange("solarCollectorModel")}
          sx={{ flex: 1 }}
        />

        <TextField
          label="Área externa (m2)"
          placeholder="Ex.: 1"
          value={heatingWaterData.externalAreaM2 || ""}
          onChange={handleFieldChange("externalAreaM2")}
          sx={{ flex: 1 }}
        />
      </Grid>

      <TextField
        fullWidth
        label="Produção média mensal de energia (kWh)"
        placeholder="Informe"
        value={heatingWaterData.averageMonthlyEnergyKwh || ""}
        onChange={handleFieldChange("averageMonthlyEnergyKwh")}
      />

      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        gap={2}
      >
        <TextField
          label="Eficiência (%)"
          placeholder="%"
          value={heatingWaterData.efficiencyPct || ""}
          onChange={handleFieldChange("efficiencyPct")}
          sx={{ flex: 1 }}
        />

        <TextField
          label="Área coletora por UC (m2)"
          placeholder="Informe"
          value={heatingWaterData.collectorAreaPerUcM2 || ""}
          onChange={handleFieldChange("collectorAreaPerUcM2")}
          sx={{ flex: 1 }}
        />
      </Grid>

      <TextField
        fullWidth
        label="Quantidade de coletores solares"
        placeholder="Informe"
        value={heatingWaterData.solarCollectorsQuantity || ""}
        onChange={handleFieldChange("solarCollectorsQuantity")}
      />

      <TextField
        fullWidth
        label="Modelo do reservatório térmico"
        placeholder="Informe"
        value={heatingWaterData.thermalReservoirModel || ""}
        onChange={handleFieldChange("thermalReservoirModel")}
      />

      <TextField
        fullWidth
        label="Volume do reservatório (L)"
        placeholder="Informe"
        value={heatingWaterData.reservoirVolumeL || ""}
        onChange={handleFieldChange("reservoirVolumeL")}
      />

      <TextField
        fullWidth
        label="Potência de aquecimento auxiliar (W)"
        placeholder="Informe"
        value={heatingWaterData.auxiliaryHeatingPowerW || ""}
        onChange={handleFieldChange("auxiliaryHeatingPowerW")}
      />

      <FormControl fullWidth>
        <InputLabel id={`select-conservation-${heatingWaterPos}`}>
          Estado de conservação
        </InputLabel>
        <Select
          labelId={`select-conservation-${heatingWaterPos}`}
          label="Estado de conservação"
          value={heatingWaterData.conservationStatus || ""}
          onChange={handleFieldChange("conservationStatus")}
        >
          {CONSERVATION_STATES.map((option) => (
            <MenuItem value={option} key={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Observações"
        placeholder="Descreva sua observação"
        value={heatingWaterData.obs || ""}
        onChange={handleFieldChange("obs")}
      />

      <PhotoUploadGrid
        photos={heatingWaterData.images}
        onPhotosChange={handlePhotosChange}
        buttonText="Enviar foto"
      />

      <Typography variant="subtitle1">Chuveiros</Typography>

      <TextField
        fullWidth
        label="Quantidade de chuveiros por UC"
        placeholder="Informe"
        value={heatingWaterData.showersPerUcQuantity || ""}
        onChange={handleFieldChange("showersPerUcQuantity")}
      />

      <TextField
        fullWidth
        label="Potência máxima das chuveiros (W)"
        placeholder="Informe"
        value={heatingWaterData.maxShowerPowerW || ""}
        onChange={handleFieldChange("maxShowerPowerW")}
      />

      <TextField
        fullWidth
        label="Fator de coincidência na ponta"
        placeholder="Informe"
        value={heatingWaterData.peakCoincidenceFactor || ""}
        onChange={handleFieldChange("peakCoincidenceFactor")}
      />

      <Typography variant="subtitle1">Banhos</Typography>

      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        gap={2}
      >
        <TextField
          label="Quantidade de banhos"
          placeholder="Informe"
          value={heatingWaterData.bathsQuantityPerDay || ""}
          onChange={handleFieldChange("bathsQuantityPerDay")}
          sx={{ flex: 1 }}
        />

        <TextField
          label="Tempo médio (min)"
          placeholder="Informe"
          value={heatingWaterData.bathsAvgTimeMin || ""}
          onChange={handleFieldChange("bathsAvgTimeMin")}
          sx={{ flex: 1 }}
        />
      </Grid>

      {onDelete && <DeleteCTA onDeleteConfirmed={onDelete} />}
    </BlockStandard>
  );
};

export type HeatingWaterDetailItem = {
  systemType: string;
  solarCollectorModel: string;
  collectorAreaPerUcM2: string;
  efficiencyPct: string;
  externalAreaM2: string;
  reservoirVolumeL: string;
  solarCollectorsQuantity: string;
  comfortEffectsQuantity: string;
  thermalReservoirModel: string;
  conservationStatus: MaintenanceState | "";
  maxShowerPowerW: string;
  bathsAvgTimeMin: string;
  showerFlowLMin: string;
  auxiliaryHeatingPowerW: string;
  bathsQuantityPerDay: string;
  peakCoincidenceFactor: string;
  averageMonthlyEnergyKwh: string;
  showersPerUcQuantity: string;
  obs: string;
  images: File[];
};

export const emptyHeatingWater: HeatingWaterDetailItem = {
  systemType: "",
  solarCollectorModel: "",
  collectorAreaPerUcM2: "",
  efficiencyPct: "",
  externalAreaM2: "",
  reservoirVolumeL: "",
  solarCollectorsQuantity: "",
  comfortEffectsQuantity: "",
  thermalReservoirModel: "",
  conservationStatus: "",
  maxShowerPowerW: "",
  bathsAvgTimeMin: "",
  showerFlowLMin: "",
  auxiliaryHeatingPowerW: "",
  bathsQuantityPerDay: "",
  peakCoincidenceFactor: "",
  averageMonthlyEnergyKwh: "",
  showersPerUcQuantity: "",
  obs: "",
  images: [],
};

export type HeatingWaterDetailProps = {
  heatingWaterData: HeatingWaterDetailItem;
  heatingWaterPos: number;
  onChange: (heatingWater: HeatingWaterDetailItem) => void;
  onDelete?: () => void;
};
